import type { ToneId } from "../i18n/tone/index.js";

/** The four earcon contours. */
export type EarconSeverity = "info" | "warning" | "critical" | "clear";

/** A fully-resolved note ready for the player to schedule. */
export interface NoteSpec {
  freq: number; // Hz
  startMs: number; // offset from playback start
  durMs: number;
  gain: number; // peak gain (already includes the severity ceiling)
  wave: OscillatorType;
  filterHz: number; // lowpass cutoff
  attackMs: number;
  releaseMs: number;
  detuneCents: number;
}

/** Base pitch ≈ C5; gap between notes. */
const BASE_HZ = 523.25;
const GAP_MS = 30;
const hz = (semitones: number): number => BASE_HZ * Math.pow(2, semitones / 12);

/** One contour step: pitch offset (semitones from base), duration, gain multiplier (0..1). */
interface ContourStep {
  semitones: number;
  durMs: number;
  gainMult: number;
}

/** Severity → contour. `ceiling` is the peak master gain for that tier. The contour
 *  (count / direction / tempo) is what carries the severity meaning and is invariant. */
const SEVERITY: Record<EarconSeverity, { ceiling: number; steps: ContourStep[] }> = {
  info: { ceiling: 0.18, steps: [{ semitones: 0, durMs: 170, gainMult: 1 }] },
  warning: {
    ceiling: 0.2,
    steps: [
      { semitones: 0, durMs: 110, gainMult: 0.9 },
      { semitones: 4, durMs: 150, gainMult: 1 },
    ],
  },
  critical: {
    ceiling: 0.28,
    steps: [
      { semitones: 7, durMs: 95, gainMult: 1 },
      { semitones: 0, durMs: 95, gainMult: 0.9 },
      { semitones: 7, durMs: 95, gainMult: 1 },
      { semitones: 0, durMs: 130, gainMult: 1 },
    ],
  },
  clear: {
    ceiling: 0.16,
    steps: [
      { semitones: 7, durMs: 130, gainMult: 0.8 },
      { semitones: 2, durMs: 120, gainMult: 0.85 },
      { semitones: 0, durMs: 220, gainMult: 0.9 },
    ],
  },
};

/** Voice → timbre. Recolors the contour; never changes its shape.
 *  `minor` recolors the major third (+4 → +3). `flatten` forces equal duration/gain. */
interface VoiceTimbre {
  wave: OscillatorType;
  filterHz: number;
  attackMs: number;
  releaseMs: number;
  detuneCents: number;
  minor: boolean;
  flatten: boolean;
}

const VOICE: Record<ToneId, VoiceTimbre> = {
  official: { wave: "sine", filterHz: 8000, attackMs: 8, releaseMs: 60, detuneCents: 0, minor: false, flatten: false },
  human: { wave: "triangle", filterHz: 2500, attackMs: 12, releaseMs: 90, detuneCents: 0, minor: false, flatten: false },
  deadpan: { wave: "sine", filterHz: 1600, attackMs: 6, releaseMs: 40, detuneCents: 0, minor: false, flatten: true },
  nonna: { wave: "sine", filterHz: 4000, attackMs: 6, releaseMs: 140, detuneCents: 0, minor: false, flatten: false },
  dark: { wave: "triangle", filterHz: 1400, attackMs: 8, releaseMs: 80, detuneCents: -8, minor: true, flatten: false },
};

const FLAT_DUR_MS = 120;
const FLAT_GAIN_MULT = 0.85;

/** Combine a severity contour with a voice timbre into concrete, schedulable notes. */
export function earconScore(severity: EarconSeverity, voice: ToneId): NoteSpec[] {
  const { ceiling, steps } = SEVERITY[severity];
  const tb = VOICE[voice];
  const out: NoteSpec[] = [];
  let t = 0;
  for (const s of steps) {
    // `dark` recolors only the major third (+4 → +3). Other intervals pass through;
    // revisit if a future contour adds +7/+9 steps that should also recolor.
    const semitones = tb.minor && s.semitones === 4 ? 3 : s.semitones;
    const durMs = tb.flatten ? FLAT_DUR_MS : s.durMs;
    const gainMult = tb.flatten ? FLAT_GAIN_MULT : s.gainMult;
    out.push({
      freq: hz(semitones),
      startMs: t,
      durMs,
      gain: ceiling * gainMult,
      wave: tb.wave,
      filterHz: tb.filterHz,
      attackMs: tb.attackMs,
      releaseMs: tb.releaseMs,
      detuneCents: tb.detuneCents,
    });
    t += durMs + GAP_MS;
  }
  return out;
}
